import { create } from "zustand";

/**
 * Privacy store — hides sensitive data during recording.
 *
 * The recording is a real tab capture, so anything drawn over the page is what ends up
 * in the video. Masks are therefore visible on screen too: what you see is what the
 * audience sees (which also protects live screen shares).
 *
 * Two complementary mechanisms:
 *  - autoPatterns: regex detection over DOM text (terminal, UI). Cannot reach iframe
 *    content (PDF, Slides, embedded browser) — the page may not read across origins.
 *  - zones: manual rectangles, which work anywhere including iframes.
 */

// Each pattern captures the secret in group 1 when a label precedes it, otherwise
// the whole match is masked.
export const PRIVACY_PATTERNS = {
  email: {
    label: "Emails",
    regex: () => /[\w.+-]+@[\w-]+\.[\w.-]{2,}/g,
  },
  ip: {
    label: "Adresses IP",
    regex: () => /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,
  },
  apiKey: {
    label: "Clés API",
    regex: () =>
      /\b(?:sk-[A-Za-z0-9_-]{16,}|gh[pousr]_[A-Za-z0-9]{20,}|AKIA[0-9A-Z]{16}|xox[baprs]-[A-Za-z0-9-]{10,}|glpat-[A-Za-z0-9_-]{20,})\b/g,
  },
  jwt: {
    label: "Tokens JWT",
    regex: () => /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}/g,
  },
  secret: {
    label: "Mots de passe / secrets",
    // Masks only the value after the label, not the label itself
    regex: () => /(?:pass(?:word)?|passwd|pwd|secret|token|api[_-]?key)\s*[:=]\s*(\S+)/gi,
  },
  privateKey: {
    label: "Clés privées",
    regex: () => /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----|-----BEGIN [A-Z ]*PRIVATE KEY-----/g,
  },
};

export const usePrivacyStore = create((set) => ({
  isEnabled: false,
  // "solid" by default: blurred text can often be reconstructed, an opaque box cannot
  style: "solid", // "solid" | "blur"
  drawing: false, // user is dragging out a new manual zone
  editingZones: false, // show delete handles on zones

  autoPatterns: {
    email: true,
    ip: false, // lab IPs are usually harmless; off by default
    apiKey: true,
    jwt: true,
    secret: true,
    privateKey: true,
  },

  // Rectangles in coordinates relative to the studio container
  zones: [],

  setEnabled: (isEnabled) => set({ isEnabled }),
  toggleEnabled: () => set((s) => ({ isEnabled: !s.isEnabled })),
  setStyle: (style) => set({ style }),
  setDrawing: (drawing) => set({ drawing }),
  setEditingZones: (editingZones) => set({ editingZones }),
  togglePattern: (key) =>
    set((s) => ({ autoPatterns: { ...s.autoPatterns, [key]: !s.autoPatterns[key] } })),
  setAutoPatterns: (autoPatterns) => set((s) => ({ autoPatterns: { ...s.autoPatterns, ...autoPatterns } })),

  addZone: (zone) =>
    set((s) => ({ zones: [...s.zones, { id: `zone_${Date.now()}`, ...zone }] })),
  removeZone: (id) => set((s) => ({ zones: s.zones.filter((z) => z.id !== id) })),
  setZones: (zones) => set({ zones: Array.isArray(zones) ? zones : [] }),
  clearZones: () => set({ zones: [] }),
}));
